/**
 * useMyActivities Hook
 *
 * Fetches customer's activities with type filtering and pagination support.
 * Uses the getMyActivities query from the CRM service.
 *
 * Requirements: Task 8 - Activity Timeline Page
 */

import { useMemo, useCallback } from 'react';
import {
  useGetMyActivitiesQuery,
  type GetMyActivitiesQuery,
  type PaginationInput,
  type ActivityFilterInput,
  type ActivityType,
} from '@bloomcode/graphql';

// Type aliases for query results
type ActivityItem = GetMyActivitiesQuery['getMyActivities']['data'][number];
type PaginationInfo = GetMyActivitiesQuery['getMyActivities']['pagination'];

interface UseMyActivitiesOptions {
  page?: number;
  limit?: number;
  typeFilter?: ActivityType[];
  skip?: boolean;
}

interface UseMyActivitiesResult {
  activities: ActivityItem[];
  pagination: PaginationInfo | null;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch the current customer's activities with type filtering and pagination
 *
 * @param options - Configuration options including page, limit, and type filter
 * @returns Customer activities data with loading and error states
 */
export function useMyActivities(options: UseMyActivitiesOptions = {}): UseMyActivitiesResult {
  const { page = 1, limit = 10, typeFilter, skip = false } = options;

  const pagination: PaginationInput = { page, limit };

  const filter: ActivityFilterInput | undefined = useMemo(() => {
    if (!typeFilter || typeFilter.length === 0) return undefined;
    return { type: typeFilter };
  }, [typeFilter]);

  const {
    data,
    loading,
    error,
    refetch: refetchQuery,
  } = useGetMyActivitiesQuery({
    variables: { pagination, filter },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const activities: ActivityItem[] = useMemo(() => data?.getMyActivities?.data ?? [], [data]);

  const paginationInfo: PaginationInfo | null = useMemo(
    () => data?.getMyActivities?.pagination ?? null,
    [data]
  );

  const totalCount = paginationInfo?.total ?? 0;

  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);

  return {
    activities,
    pagination: paginationInfo,
    totalCount,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

export type { ActivityItem, PaginationInfo };
export default useMyActivities;
